import { useState } from 'react';
import { Alert, ControlLabel, FormControl, FormGroup } from 'react-bootstrap';
import { FilterCard, opoCardStyles, opoTableStyles } from './opo-styles';
import { ILogistics, IPendingSwap } from '@nkr/models/index';
import { SwapsToShow } from '@nkr/views/center-admin/action-center/models';
import styled from 'styled-components';
import useOpoSwaps from '@nkr/store/opo/hooks/use-opo-swaps';
import Donors from '@nkr/views/center-admin/action-center/pending-swaps/donors';
import RemoteDonors from '@nkr/views/center-admin/action-center/pending-swaps/remote-donors';
import LogisticsView from '@nkr/views/swap-management/swaps/swap-list/logistics/view';

const SwapPanels = styled.div`
    .panel {
        ${opoCardStyles}
        margin-bottom: 14px;
        box-shadow: none;
    }

    .panel > .panel-heading {
        padding: 12px 16px;
        background-color: #fff;
        border-bottom: 1px solid #E3E8EF;
        color: #16365A;
        font-size: 14px !important;
        font-weight: 600;
    }

    .panel > .panel-body {
        padding: 0;
    }

    .panel-body > .table {
        margin-bottom: 0;
        ${opoTableStyles}
    }

    .panel-body > .table > tbody > tr > td > .btn-xs {
        border-radius: 6px;
        font-weight: 600;
    }

    .panel-body > .table > tbody > tr > td[colspan] {
        padding: 24px 16px !important;
        color: #6B7A8F;
        font-size: 14px !important;
    }
`;

const timeframes = [
    { value: 3, title: 'Past 3 Months' },
    { value: 6, title: 'Past 6 Months' },
    { value: 9, title: 'Past 9 Months' },
    { value: 12, title: 'Past 12 Months' },
    { value: 999, title: 'All Prior Swaps' },
];

const widths = ['6%', '5%', '5%', '', '', '', '7%', '5%', '10%', '5%', '5%', '7%', '7%', '7%'];

const noop = () => null;

const OpoSwaps = () => {
    const [show, setShow] = useState(SwapsToShow.Pending);
    const [timeframe, setTimeframe] = useState(3);
    const [logistics, setLogistics] = useState<{ show: boolean, data: ILogistics | null }>({ show: false, data: null });
    const { swaps, loading, error } = useOpoSwaps(show == SwapsToShow.Past, timeframe);

    const currentTimeframe = timeframes.find(x => x.value == timeframe);

    const handlers = {
        onRequestScreeningXm: noop,
        onRequestFinalXm: noop,
        onEmergencyNotification: noop,
        onRequestSerologyKit: noop,
        onUploadFinalXm: noop,
        onUploadSerology: noop,
        onViewCustomLogistics: noop,
        onGetFinalXmResults: noop,
        onDownloadFormsAndChecklists: noop,
        onGetFile: noop,
        onGetPatientOutcomes: noop,
        onViewCtScan: noop,
        onViewLogistics: (swap: IPendingSwap) => setLogistics({ show: true, data: swap.logistics }),
        onShowMap: (url: string) => window.open(url),
        onShowGps: (url: string) => window.open(url)
    };

    return (
        <>
            <FilterCard>
                <div className='search-row'>
                    <FormGroup>
                        <ControlLabel>Swaps</ControlLabel>
                        <div className='search-toggles'>
                            <button type='button' className={show == SwapsToShow.Pending ? 'btn active' : 'btn'} onClick={() => setShow(SwapsToShow.Pending)}>Pending Swaps</button>
                            <button type='button' className={show == SwapsToShow.Past ? 'btn active' : 'btn'} onClick={() => setShow(SwapsToShow.Past)}>Past Swaps</button>
                        </div>
                    </FormGroup>

                    {show == SwapsToShow.Past && (
                        <FormGroup style={{ flex: '0 0 170px' }}>
                            <ControlLabel>Timeframe</ControlLabel>
                            <FormControl componentClass='select' value={timeframe} onChange={(e: any) => setTimeframe(Number(e.target.value))}>
                                {timeframes.map(x =>
                                    <option key={x.value} value={x.value}>{x.title}</option>
                                )}
                            </FormControl>
                        </FormGroup>
                    )}

                    <div className='search-stats'>
                        <div className='search-stat'>
                            <span className='k'>Donors</span>
                            <span className='v'>{loading ? '–' : swaps.donorSwaps.length}</span>
                        </div>
                        <div className='search-stat'>
                            <span className='k'>Remote Donors</span>
                            <span className='v'>{loading ? '–' : swaps.remoteDonors.length}</span>
                        </div>
                    </div>
                </div>
            </FilterCard>

            {error && (
                <Alert bsStyle='danger'>{error}</Alert>
            )}

            {!loading && (
                <SwapPanels>
                    <Donors
                        list={swaps.donorSwaps}
                        centerId={0}
                        show={show}
                        currentTimeframe={currentTimeframe}
                        widths={widths}
                        readOnly={true}
                        {...handlers}
                    />

                    <RemoteDonors
                        list={swaps.remoteDonors}
                        centerId={0}
                        show={show}
                        currentTimeframe={currentTimeframe}
                        widths={widths}
                        title='Remote Donors'
                        readOnly={true}
                        {...handlers}
                    />
                </SwapPanels>
            )}

            <LogisticsView
                show={logistics.show}
                data={logistics.data}
                fontSize={13}
                onClose={() => setLogistics({ show: false, data: null })}
            />
        </>
    );
}

export default OpoSwaps;
